/**
 * Real-time presence tracking for collaborative editing.
 *
 * Tracks:
 *   - Which users are online in a project
 *   - Active file per user
 *   - Cursor position and selection
 *   - Color assignment (deterministic from userId)
 *
 * Writes presence to: projects/{projectId}/presence/{userId}
 * Subscribes to all presence docs via onSnapshot.
 */

import { useState, useEffect, useRef, useCallback } from 'react';
import {
  collection,
  doc,
  setDoc,
  onSnapshot,
  Timestamp,
} from 'firebase/firestore';
import { db } from '../services/firebase';
import { UserPresence } from '../types/ot';

const CURSOR_THROTTLE_MS = 100;
const HEARTBEAT_INTERVAL_MS = 15_000;
const STALE_THRESHOLD_MS = 30_000;

/** Deterministic color assignment from user ID hash */
const PRESENCE_COLORS = [
  '#e74c3c', '#3498db', '#2ecc71', '#f39c12',
  '#9b59b6', '#1abc9c', '#e67e22', '#34495e',
  '#16a085', '#c0392b', '#2980b9', '#27ae60',
];

function userIdToColor(userId: string): string {
  let hash = 0;
  for (let i = 0; i < userId.length; i++) {
    hash = ((hash << 5) - hash + userId.charCodeAt(i)) | 0;
  }
  return PRESENCE_COLORS[Math.abs(hash) % PRESENCE_COLORS.length];
}

/** Get initials from display name */
function getInitials(displayName: string | null): string {
  if (!displayName) return '?';
  const parts = displayName.trim().split(/\s+/);
  if (parts.length >= 2) return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
  return parts[0].slice(0, 2).toUpperCase();
}

interface UsePresenceOptions {
  projectId: string | undefined;
  userId: string | undefined;
  displayName: string | null;
  activeFileId: string | null;
  enabled: boolean;
}

interface UsePresenceResult {
  /** All online users (excluding self) */
  remoteUsers: UserPresence[];
  /** Update local cursor position (throttled) */
  updateCursor: (cursor: { lineNumber: number; column: number } | null) => void;
  /** Update local selection (throttled) */
  updateSelection: (selection: {
    startLineNumber: number;
    startColumn: number;
    endLineNumber: number;
    endColumn: number;
  } | null) => void;
}

export function usePresence({
  projectId,
  userId,
  displayName,
  activeFileId,
  enabled,
}: UsePresenceOptions): UsePresenceResult {
  const [remoteUsers, setRemoteUsers] = useState<UserPresence[]>([]);

  const cursorRef = useRef<UserPresence['cursor']>(null);
  const selectionRef = useRef<UserPresence['selection']>(null);
  const throttleTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const heartbeatTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // Write presence document
  const writePresence = useCallback(
    async (partial: Partial<UserPresence>) => {
      if (!projectId || !userId) return;
      const presenceRef = doc(db, 'projects', projectId, 'presence', userId);
      try {
        await setDoc(presenceRef, {
          userId,
          displayName: displayName || 'Anonymous',
          color: userIdToColor(userId),
          activeFileId: activeFileId ?? null,
          cursor: cursorRef.current ?? null,
          selection: selectionRef.current ?? null,
          lastSeen: Timestamp.now(),
          online: true,
          ...partial,
        }, { merge: true });
      } catch (err) {
        // Silently ignore presence write failures — non-critical
        console.warn('Presence write failed:', err);
      }
    },
    [projectId, userId, displayName, activeFileId]
  );

  // Mark offline on unmount/close
  const markOffline = useCallback(async () => {
    if (!projectId || !userId) return;
    const presenceRef = doc(db, 'projects', projectId, 'presence', userId);
    try {
      await setDoc(presenceRef, {
        online: false,
        lastSeen: Timestamp.now(),
        cursor: null,
        selection: null,
      }, { merge: true });
    } catch {
      // Best effort
    }
  }, [projectId, userId]);

  // Initialize presence and heartbeat
  useEffect(() => {
    if (!enabled || !projectId || !userId) return;

    // Write initial presence
    writePresence({ online: true });

    // Heartbeat
    heartbeatTimerRef.current = setInterval(() => {
      writePresence({ online: true });
    }, HEARTBEAT_INTERVAL_MS);

    // beforeunload handler
    const handleUnload = () => {
      // Use sendBeacon for reliable offline marking
      markOffline();
    };
    window.addEventListener('beforeunload', handleUnload);

    return () => {
      window.removeEventListener('beforeunload', handleUnload);
      if (heartbeatTimerRef.current) clearInterval(heartbeatTimerRef.current);
      markOffline();
    };
  }, [enabled, projectId, userId, writePresence, markOffline]);

  // Update presence when active file changes
  useEffect(() => {
    if (!enabled || !projectId || !userId) return;
    writePresence({ activeFileId: activeFileId ?? null });
  }, [enabled, projectId, userId, activeFileId, writePresence]);

  // Subscribe to all presence docs
  useEffect(() => {
    if (!enabled || !projectId || !userId) return;

    const presenceCol = collection(db, 'projects', projectId, 'presence');
    const unsub = onSnapshot(presenceCol, (snapshot) => {
      const now = Date.now();
      const users: UserPresence[] = [];

      for (const docSnap of snapshot.docs) {
        if (docSnap.id === userId) continue; // Skip self
        const data = docSnap.data();
        const lastSeen = data.lastSeen?.toDate?.()?.getTime() ?? 0;

        // Filter out stale users
        if (!data.online || now - lastSeen > STALE_THRESHOLD_MS) continue;

        users.push({
          userId: docSnap.id,
          displayName: data.displayName || 'Anonymous',
          color: data.color || userIdToColor(docSnap.id),
          activeFileId: data.activeFileId ?? null,
          cursor: data.cursor ?? null,
          selection: data.selection ?? null,
          lastSeen: data.lastSeen?.toDate() ?? new Date(),
          online: data.online ?? false,
        });
      }

      setRemoteUsers(users);
    });

    return () => unsub();
  }, [enabled, projectId, userId]);

  // Throttled cursor update
  const updateCursor = useCallback(
    (cursor: UserPresence['cursor']) => {
      cursorRef.current = cursor;
      if (throttleTimerRef.current) return; // Already scheduled
      throttleTimerRef.current = setTimeout(() => {
        throttleTimerRef.current = null;
        writePresence({ cursor: cursorRef.current });
      }, CURSOR_THROTTLE_MS);
    },
    [writePresence]
  );

  // Throttled selection update
  const updateSelection = useCallback(
    (selection: UserPresence['selection']) => {
      selectionRef.current = selection;
      if (throttleTimerRef.current) return;
      throttleTimerRef.current = setTimeout(() => {
        throttleTimerRef.current = null;
        writePresence({ selection: selectionRef.current });
      }, CURSOR_THROTTLE_MS);
    },
    [writePresence]
  );

  return { remoteUsers, updateCursor, updateSelection };
}

export { userIdToColor, getInitials };
